// T297 (6 Oct 2026, Natanel): "going back should take me back to the previous page i was on before
// i clicked on it to get there." The link at the top of a page used to go to one fixed page, so a
// conversation opened from Proactive went back to the Conversations tab. It now reads the browser's
// own history list (the Navigation API) and goes to the nearest earlier entry that is another page,
// naming it. Nothing is remembered by the app: the first build kept its own trail of where each
// click came from, and review round 2 found it pointing a saved edit back at itself; Natanel:
// "Redesign that piece". A browser without the API keeps the fixed link.

import { matchPath } from 'react-router-dom';

/** A page to go back to: its path and query, and how many history entries back it is. */
export interface Back {
  path: string;
  search: string;
  steps: number;
}

/**
 * The nearest entry before `index` in the history list `urls` that is another page — another
 * path; the same path with another query (a comparison with one person fewer, an edit saved over
 * the page) is the same page, and a form is never one (`isForm`): the link goes past both, and past
 * an entry the browser gives no address. `null`
 * when there is none, or it is not this app's (another origin, which the browser does not list).
 */
export function previousPlace(
  urls: readonly (string | null)[],
  index: number,
  origin: string,
): Back | null {
  if (index < 1 || index >= urls.length || !urls[index]) return null;
  const path = new URL(urls[index] as string, origin).pathname;
  for (let at = index - 1; at >= 0; at -= 1) {
    if (!urls[at]) continue; // an entry with no address (null), not the home page
    const url = new URL(urls[at] as string, origin);
    if (url.origin !== origin) return null;
    if (url.pathname !== path && !isForm(url.pathname)) {
      return { path: url.pathname, search: url.search, steps: index - at };
    }
  }
  return null;
}

/** Where the router says this page is. */
export interface Here {
  pathname: string;
  search: string;
}

/**
 * The page to go back to, read once for the router's `here`: `null` when the history list is not
 * at `here` — the browser has moved on and the router has not drawn the new page yet, so the old
 * page keeps its fixed link rather than borrowing the new entry's — or there is none.
 */
export function backFrom(list: HistoryList | null, here: Here): Back | null {
  if (!list) return null;
  const now = new URL(list.urls[list.index] ?? '', list.origin);
  if (now.pathname !== here.pathname || now.search !== here.search) return null;
  return previousPlace(list.urls, list.index, list.origin);
}

/** A click a back link takes for itself: a plain one, when there is a page to go back to. A
 * modified one (a new tab) or a middle button keeps the link's own address. */
export function takesClick(
  event: { metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean; button: number },
  hasBack: boolean,
): boolean {
  if (!hasBack) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  return event.button === 0;
}

/** The slice of the Navigation API this reads; not in every TypeScript DOM library yet. */
interface HistoryNavigation {
  currentEntry: { index: number } | null;
  entries(): { url: string | null }[];
}

/** The app's two forms, as `App.tsx` routes them — the one list `isForm` reads. */
export const FORM_ROUTES = {
  new: 'candidates/new',
  edit: 'candidates/:candidateId/edit',
} as const;

const FORM_PATTERNS = Object.values(FORM_ROUTES).map((path) => ({ path }));

/**
 * A form is never somewhere to go back to (Natanel, 6 Oct 2026: "Add that rule"): going back to
 * New candidate after the person was filed would offer the blank form to file them twice. Read
 * off the routes by the router's own matcher, so a case the router accepts is a form here too.
 */
export function isForm(path: string): boolean {
  return FORM_PATTERNS.some((pattern) => matchPath(pattern, path) !== null);
}

/** The browser's history list and where this page is in it. */
export interface HistoryList {
  urls: (string | null)[];
  index: number;
  origin: string;
}

/** The history list, or `null` without the Navigation API. */
export function historyList(): HistoryList | null {
  if (typeof window === 'undefined') return null;
  const navigation = (window as Window & { navigation?: HistoryNavigation }).navigation;
  const current = navigation?.currentEntry;
  if (!navigation || !current || current.index < 0) return null;
  return {
    urls: navigation.entries().map((entry) => entry.url ?? null),
    index: current.index,
    origin: window.location.origin,
  };
}

/**
 * What a page is called when a back link names it — the noun the side menu and the page's own
 * title use. `null` for a path this does not know, and the link then keeps its fixed word.
 */
export function placeLabel(path: string): string | null {
  const parts = path.split('/').filter(Boolean);
  const [a, b, c, d] = parts;
  switch (a) {
    case 'searches':
      return b === undefined ? 'Searches' : c === 'compare' ? 'Comparison' : 'Search';
    case 'candidates':
      if (b === undefined) return 'Candidates';
      if (b === 'proactive') return 'Proactive';
      if (b === 'no-mans-land') return 'No-man’s-land';
      if (c === 'profile') return 'Profile';
      return 'Candidate';
    case 'conversations':
      return b === undefined ? 'Conversations' : 'Conversation';
    case 'proposed':
      return 'Proposed';
    case 'crm':
      if (b === 'clients' && c !== undefined) return d === 'contacts' ? 'Contact' : 'Company';
      return (
        {
          clients: 'Clients',
          potential: 'Potential clients',
          sales: 'Sales',
          connections: 'Connections',
        }[b ?? ''] ?? 'CRM'
      );
    case 'research':
      return b === undefined ? 'Research' : 'Research run';
    case 'meetings':
      if (b === undefined) return 'Meetings';
      if (b === 'team') return c === undefined ? 'Team meetings' : 'Team meeting';
      return (
        {
          candidates: 'Candidate meetings',
          clients: 'Client meetings',
          potential: 'Potential client meetings',
          sales: 'Sales meetings',
          investors: 'Investor meetings',
          advisors: 'Advisor meetings',
          nomads: 'Nomad meetings',
          demos: 'Demos',
          lawyer: 'Lawyer meetings',
          reader: 'Meeting reader',
        }[b] ?? 'Meeting'
      );
    case 'client-documents':
      return 'Document';
    case 'staff':
      return 'Colleague';
    case 'reports':
      return 'Reports';
    case 'settings':
      return 'Settings';
    default:
      return null;
  }
}
